import React from "react";
import {
  MdCheck,
  MdOutlineStickyNote2,
  MdOutlineWarningAmber,
} from "react-icons/md";
import SheetChip from "./SheetChip";
import { productName } from "./routeSheet";

const PANEL =
  "overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface";
// Phones: headings wrap and read a little larger. From md up, and on paper
// (the printed page is narrower than md), as before.
const PANEL_HEAD =
  "flex [flex-wrap:wrap] items-center gap-[8px] border-b-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[7px] text-[12px] font-bold text-ui-text md:flex-nowrap md:text-[11.5px] print:flex-nowrap print:text-[11.5px]";
const PANEL_HINT =
  "ml-auto text-[12px] font-medium text-ui-text-muted md:text-[10.5px] print:text-[10.5px]";
// The "To pick" table: on phones each row is a card (tick box | product |
// how many, then its stops and Missing? under the product); from md up and
// on paper it is the table.
const CELL =
  "block p-[0px] md:table-cell md:px-[10px] md:py-[9px] print:table-cell print:px-[10px] print:py-[9px]";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const lineName = (line) => productName(line.item) || "-";

/**
 * The pick list: what to pick for the route sheet's stops, product by
 * product — a Picked tick, the product (SKU, weight, the pallets that hold
 * it at the order's location with what each holds, and each order's note on
 * it from the shipper), how many to pick,
 * the stops it's for, and a Missing? report for anything not on the shelf.
 * Lines already being packed or fulfilled are listed apart so nobody picks
 * them again. Ticks and reports come from `picks` ({ picked, missing } sets
 * of order line ids); `onPicked` / `onMissing` toggle them for a row.
 */
const PickList = ({ list, picks, onPicked, onMissing, formatLb }) => {
  const { toPick, packing, fulfilled } = list;
  const isPicked = (row) => row.lineIds.every((id) => picks.picked.has(id));
  const isMissing = (row) => row.lineIds.some((id) => picks.missing.has(id));
  // Being saved in Bolt: the tick waits for the answer.
  const isBusy = (row) =>
    Boolean(picks.busy) && row.lineIds.some((id) => picks.busy.has(String(id)));
  const pieces = toPick.reduce((sum, row) => sum + row.quantity, 0);
  const done = toPick.filter(isPicked).length;
  const missing = toPick.filter(isMissing);

  return (
    <div className="grid gap-[10px]">
      <div className="flex flex-wrap items-center gap-[8px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[8px]">
        <span
          role="progressbar"
          aria-label="Picked"
          aria-valuemin={0}
          aria-valuemax={toPick.length}
          aria-valuenow={done}
          className="block h-[6px] w-[120px] overflow-hidden rounded-full bg-ui-border"
        >
          <span
            className="block h-full rounded-full bg-brand"
            style={{
              width: `${toPick.length ? Math.round((done / toPick.length) * 100) : 0}%`,
            }}
          />
        </span>
        <span className="text-[12px] text-ui-text-muted md:text-[11.5px] print:text-[11.5px]">
          <b className="text-ui-text">
            {done} of {toPick.length}
          </b>{" "}
          products picked
        </span>
        <SheetChip tone="neutral">{pieces} pcs to pick</SheetChip>
        {missing.length > 0 && (
          <SheetChip tone="danger">
            <MdOutlineWarningAmber className="h-[11px] w-[11px]" />
            {plural(missing.length, "product")} reported missing
          </SheetChip>
        )}
      </div>

      {missing.length > 0 && (
        <div
          role="status"
          className="rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[9px] text-[12px] text-state-danger-text"
        >
          <b>Reported missing:</b>{" "}
          {missing
            .map(
              (row) =>
                `${row.name} (stop${row.stops.length > 1 ? "s" : ""} ${row.stops
                  .map((stop) => stop.number)
                  .join(", ")})`,
            )
            .join("; ")}
          . Those stops can't be completed in full on this run.
        </div>
      )}

      <section className={PANEL}>
        <div className={PANEL_HEAD}>To pick</div>
        {toPick.length ? (
          <div className="overflow-x-auto">
            {/* Words stay whole here: the app's `* { overflow-wrap: anywhere }`
                would let the columns shrink to a letter wide. */}
            <table className="block w-full border-collapse text-left text-[12px] [overflow-wrap:break-word] md:table md:min-w-[640px] print:table print:min-w-[640px] [&_*]:[overflow-wrap:break-word]">
              <thead className="hidden md:table-header-group print:table-header-group">
                <tr className="whitespace-nowrap border-b-[1px] border-ui-border text-[10.5px] uppercase tracking-[0.04em] text-ui-text-muted">
                  <th className="w-[36px] px-[10px] py-[7px]">
                    <span className="sr-only">Picked</span>
                  </th>
                  <th className="px-[10px] py-[7px] font-bold">Product</th>
                  <th className="px-[10px] py-[7px] text-right font-bold">
                    Pick
                  </th>
                  <th className="px-[10px] py-[7px] font-bold">For stops</th>
                  <th className="px-[10px] py-[7px] print:hidden">
                    <span className="sr-only">Report</span>
                  </th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group print:table-row-group">
                {toPick.map((row) => {
                  const picked = isPicked(row);
                  const reported = isMissing(row);
                  const id = `pick-${row.key.replace(/[^a-z0-9]/gi, "")}`;
                  return (
                    <tr
                      key={row.key}
                      className={`grid grid-cols-[40px_minmax(0,1fr)_auto] gap-x-[8px] gap-y-[6px] border-b-[1px] border-ui-border-subtle px-[6px] py-[8px] align-top last:border-b-0 md:table-row print:table-row ${
                        reported
                          ? "bg-state-danger-bg"
                          : picked
                            ? "text-ui-text-muted"
                            : ""
                      }`}
                    >
                      <td className={CELL}>
                        {/* Below lg (and on touch screens) the label round
                            the tick box makes a 40px tap target; from lg up
                            and on paper it has no box. min-w-[0px]: the card
                            form's global `label { min-width: 105px }` would
                            push the box over the product name. */}
                        <label
                          htmlFor={id}
                          className="flex h-[40px] w-[40px] min-w-[0px] cursor-pointer items-center justify-center lg:contents print:contents [@media_screen_and_(pointer:coarse)]:flex"
                        >
                          <input
                            id={id}
                            type="checkbox"
                            checked={picked}
                            disabled={isBusy(row)}
                            aria-busy={isBusy(row) || undefined}
                            onChange={() => onPicked(row)}
                            className="h-[20px] w-[20px] cursor-pointer accent-brand lg:h-[16px] lg:w-[16px] print:h-[16px] print:w-[16px]"
                          />
                        </label>
                      </td>
                      <td className={`${CELL} min-w-0`}>
                        <label htmlFor={id} className="grid cursor-pointer">
                          <span className="font-bold">{row.name}</span>
                          <span className="text-ui-text-muted">
                            {[
                              row.sku,
                              row.weight != null && formatLb(row.weight),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </label>
                        {row.unlinked && (
                          <SheetChip tone="danger">Not in Products</SheetChip>
                        )}
                        {row.pallets.map((pallet) => (
                          <span
                            key={pallet.id}
                            className="block text-[12px] text-ui-text-muted md:text-[11px] print:text-[11px]"
                          >
                            From {pallet.name} ({pallet.quantity})
                            {pallet.location ? ` · ${pallet.location}` : ""}
                            {pallet.lot ? ` · Lot ${pallet.lot}` : ""}
                          </span>
                        ))}
                        {row.stops
                          .filter((stop) => stop.note)
                          .map((stop) => (
                            <span
                              key={`note-${stop.number}-${stop.orderId}`}
                              className="mt-[3px] flex items-start gap-[4px] rounded-[4px] bg-state-warning-bg px-[6px] py-[2px] text-[12px] text-state-warning-text md:text-[11px] print:text-[11px]"
                            >
                              <MdOutlineStickyNote2
                                aria-hidden="true"
                                className="mt-[2px] h-[11px] w-[11px] shrink-0"
                              />
                              <span>
                                <b>
                                  Stop {stop.number} · #{stop.orderId}:
                                </b>{" "}
                                {stop.note}
                              </span>
                            </span>
                          ))}
                      </td>
                      {/* Phones: the column names show before these values. */}
                      <td
                        className={`${CELL} text-right text-[14px] font-bold before:mr-[4px] before:text-[12px] before:font-semibold before:text-ui-text-muted before:content-['Pick'] md:before:content-none print:before:content-none`}
                      >
                        {row.quantity}
                      </td>
                      <td
                        className={`${CELL} col-span-2 col-start-2 before:mb-[4px] before:block before:text-[12px] before:font-semibold before:text-ui-text-muted before:content-['For_stops'] md:before:content-none print:before:content-none`}
                      >
                        <div className="flex flex-wrap gap-[4px]">
                          {row.stops.map((stop) => (
                            <span
                              key={`${stop.number}-${stop.orderId}`}
                              title={
                                stop.attention
                                  ? "Attention: only what's still pending"
                                  : undefined
                              }
                              className="inline-flex items-center gap-[4px] rounded-[4px] border-[1px] border-ui-border px-[5px] py-[1px] text-[12px] md:text-[11px] print:text-[11px]"
                            >
                              <b>{stop.number}</b>
                              {`#${stop.orderId} ×${stop.quantity}`}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="col-span-2 col-start-2 block p-[0px] text-left md:table-cell md:px-[10px] md:py-[9px] md:text-right print:hidden">
                        {/* Phones: an outlined 40px button. From md up the
                            text link, still 40px tall until lg. */}
                        <button
                          type="button"
                          aria-pressed={reported}
                          onClick={() => onMissing(row)}
                          aria-label={`${reported ? "Not missing" : "Missing"}: ${row.name}`}
                          className={`inline-flex min-h-[40px] items-center whitespace-nowrap rounded-[6px] border-[1px] border-ui-border bg-transparent px-[12px] text-[12px] font-semibold hover:underline md:rounded-none md:border-0 md:px-0 md:text-[11px] lg:inline-block lg:min-h-0 [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-[40px] ${
                            reported
                              ? "text-state-danger-text"
                              : "text-ui-text-muted"
                          }`}
                        >
                          {reported ? "Not missing" : "Missing?"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="m-[0px] px-[12px] py-[14px] text-ui-text-muted">
            Nothing left to pick for these stops.
          </p>
        )}
      </section>

      {packing.length > 0 && (
        <section className={PANEL}>
          <div className={PANEL_HEAD}>
            Being packed
            <span className={PANEL_HINT}>
              Already picked: finish packing and load
            </span>
          </div>
          <ul className="m-[0px] grid list-none gap-[4px] p-[10px]">
            {packing.map(({ number, order, line }) => (
              <li
                key={`${number}-${line.item?.id}`}
                className="flex flex-wrap items-center gap-[8px]"
              >
                <SheetChip tone="info">Packing</SheetChip>
                <span>
                  {line.quantity} × {lineName(line)}
                </span>
                <span className="text-ui-text-muted">
                  Stop {number} · #{order?.id}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {fulfilled.length > 0 && (
        <section className={PANEL}>
          <div className={PANEL_HEAD}>
            Already fulfilled
            <span className={PANEL_HINT}>Don't pick again</span>
          </div>
          <ul className="m-[0px] grid list-none gap-[4px] p-[10px]">
            {fulfilled.map(({ number, order, line }) => (
              <li
                key={`${number}-${line.item?.id}`}
                className="flex flex-wrap items-center gap-[8px]"
              >
                <SheetChip tone="success">
                  <MdCheck className="h-[11px] w-[11px]" />
                  Fulfilled
                </SheetChip>
                <span className="text-ui-text-muted line-through">
                  {line.quantity} × {lineName(line)}
                </span>
                <span className="text-ui-text-muted">
                  Stop {number} · #{order?.id}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default PickList;
